import { ILinks2 } from '../Types';

export interface IGroup {
  id: string;
  name: string;
  description?: string;
  app_key: string;
  org_key: string;
  member_count: number;
  schedule?: string;
  location?: string;
  frequency?: string;
  day_of_week?: string;
  time_of_day?: number | null;
  timezone?: string;
  scheduleTitle?: string;
  type: string;
  enrollment_type: IEnrollmentType;
  public: boolean;
  created_at: Date;
  updated_at: Date;
  _embedded?: {
    image?: {
      id: string;
      average_color_hex?: string;
    };
    'group-type'?: IGroupType;
    'group-members'?: IGroupMember[];
    type?: IGroupType;
    location?: { id: string };
    event?: { id: string };
    calendar?: { id: string };
  };
}

export interface IGroupType {
  id: string;
  name: string;
}

export type IEnrollmentType = 'open' | 'closed' | 'request_to_join';

export interface IGroupTypes {
  count: number;
  _embedded?: {
    'group-types': IGroupType[];
  };
}

export interface IGroupLocation {
  app_key: string;
  title: string;
  subtitle: string;
  type: string[];
  id: string;
  _embedded: {
    address: {
      id: string;
      city: string;
      latitude: number;
      longitude: number;
      state: string;
      street: string;
    };
  };
}

export interface IGroupAddress {
  _embedded: {
    manager: {
      id: string;
    };
  };
  app_key: string;
  city: string;
  country: string;
  country_obj: {
    code: string;
    name: string;
  };
  name: string;
  created_at: string;
  deleted_at: string;
  id: string;
  latitude: number;
  longitude: number;
  org_key: string;
  postal_code: string;
  state: string;
  street: string;
  street2: string;
  updated_at: string;
}

export interface IGroupMember {
  id: string;
  role: 'member' | 'manager';
  messaging_status: 'invited' | 'active' | 'banned' | 'error' | undefined;
  created_at: Date;
  updated_at: Date;
  _embedded: {
    group?: IGroup;
    'end-user'?: IEndUser;
  };
}

export interface IGroupMembers {
  count: number;
  total: number;
  _embedded?: {
    'group-members': IGroupMember[];
  };
}

export interface IEndUser {
  _links: {
    self: {
      href: string;
    };
  };
  created_at: string;
  email: string;
  first_name: string;
  id: string;
  last_name: string;
  updated_at: string;
  _embedded?: {
    profiles: IProfile[];
  };
}

export interface IProfile {
  id: string;
  short_id: number;
  org_key: string;
  first_name?: string;
  last_name?: string;
  emails: IEmail[];
  email?: string;
  phone?: string;
  photo_url?: string;
  status: 'active' | unknown;
  created_at: Date;
  updated_at: Date;
  _embedded?: {
    ['end-user']?: {
      id?: string;
    };
    photo?: {
      id?: string;
    };
  };
}

export interface IEmail {
  value: string;
}

export const GroupRoles = {
  Manager: 'manager',
  Member: 'member',
};

export interface IJoinRequest {
  id: string;
  status: 'pending' | 'approved' | 'rejected' | 'denied';
  created_at: Date;
  updated_at: Date;
  _embedded: {
    'requesting-user': IEndUser;
    group: IGroup;
    'responding-manager'?: IGroupMember;
  };
}

export interface IJoinRequests {
  count: number;
  _embedded?: {
    'join-requests': IJoinRequest[];
  };
}
export interface ISession {
  id: string;
  app_key: string;
  title: string;
  type: string;
  start_at: string;
  end_at: string;
  created_at: string;
  updated_at: string;
  check_in_start_at?: string;
  check_in_end_at?: string;
  check_in_minutes_before_start?: number;
  check_in_minutes_after_start?: number;
  suggestion_type?: string;
  hidden?: boolean;
  note?: string;
  capacity?: Record<string, any>;
  _links?: {
    self: {
      href: string;
    };
  };
  _embedded?: {
    event: {
      id: string;
    };
    counts?: {
      id: string;
      active_check_ins?: number;
      attended?: number;
      missed?: number;
      total?: number;
      total_check_ins?: number;
      total_headcount?: number;
    };
  };
}
export type IGroupEvent = {
  _links?: {
    self: {
      href: string;
    };
  };
  all_day?: boolean;
  app_key?: string;
  check_in_enabled?: boolean;
  created_at?: string;
  description?: string;
  email?: string;
  end_at?: string;
  id?: string;
  phone?: string;
  published?: boolean;
  published_at?: string;
  recurring?: boolean;
  short_code?: string;
  start_at?: string;
  status?: string;
  subtitle?: string;
  timezone?: string;
  title?: string;
  event_title?: string;
  event_duration?: number | undefined;
  updated_at?: string;
  visibility?: string;
  repetition_rules?: any;
  send_attendance_reminder?: boolean;
  _embedded?: {
    calendar?: Partial<IGroupEventsCalendar>;
    'repeating-event'?: { id: string; repetition_rules: string[] };
    event?: IGroupEvent[];
    sessions?: ISession[];
    location?:
      | IGroupLocation
      | {
          id: string | undefined;
        }
      | null;
    images?: IGroupEventsImage[];
    group?: { id: string };
  };
};

export type IGroupEventsCalendar = {
  _embedded?: {
    images?: { type: string; id: string }[];
  };
  _links: {
    self: {
      href: string;
    };
  };
  app_key: string;
  color: string;
  created_at: string;
  id: string;
  published: boolean;
  published_at?: string;
  short_code: string;
  status: string;
  title: string;
  updated_at: string;
};

export interface IGroupEventsImage {
  _embedded?: any;
  _links: ILinks2;
  app_key: string;
  average_color_hex: string;
  content_type?: string;
  created_at: string;
  file_size?: number;
  height: number;
  id: string;
  is_stock?: boolean;
  md5?: string;
  s3_object_key?: string;
  title: string;
  type: string;
  updated_at: string;
  uploaded_at?: string;
  vibrant_color_hex: string;
  width: number;
}
export interface IGroupHeadCounts {
  count: number;
  _embedded: {
    headcounts: IHeadCount[];
  };
}

export interface IHeadCount {
  id: string;
  app_key: string;
  count: number;
  org_key: string;
  _embedded: IHeadCountEmbedded;
}

export interface IHeadCountEmbedded {
  event: { id: string };
  session: { id: string };
  'repeating-event': { id: string };
}

export interface IGroupAttendances {
  _links: {
    self: { href: string };
    first: { href: string };
    prev: { href: string | null };
    next: { href: string };
    last: { href: string };
  };
  total: number;
  count: number;
  _embedded: {
    attendances: IAttendance[];
  };
}

export interface IAttendance {
  _links: {
    self: {
      href: string;
    };
  };
  id: string;
  app_key: string;
  attended: boolean;
  created_at: string;
  updated_at: string;
  org_key: string;
  _embedded: IAttendanceEmbedded;
}

export interface IAttendanceEmbedded {
  'end-user': { id: string };
  event: { id: string };
  profile: { id: string };
  'repeating-event': { id: string };
  session: { id: string };
}
